import { useState, useEffect, memo } from 'react';
import { PlusIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { PlantVariety } from '../../types/plant';
import { plantApi } from '../../lib/plantApi';
import { Button, ComboBox, Badge, Select } from '../ui';
import type { ComboBoxOption } from '../ui';
import { AddPlantVarietyModal } from './AddPlantVarietyModal';
import { useSearchInput } from '../../hooks/useSearchInput';
import { useDebounce } from '../../hooks/useDebounce';

// Badge-relevant tags for filtering
const BADGE_TAGS = [
  'native',
  'heirloom',
  'rare',
  'endangered',
  'threatened',
  'heritage',
  'historic',
  'unusual',
  'exotic',
  'experimental',
  'shared',
  'donated',
  'seed-swap',
  'award-winner',
  'record-holder',
] as const;

interface PlantVarietySearchProps {
  value?: PlantVariety | null;
  onChange: (variety: PlantVariety | null) => void;
  error?: string;
  placeholder?: string;
  showFilters?: boolean;
}

function PlantVarietySearchComponent({
  value,
  onChange,
  error,
  placeholder = 'Search for a plant variety...',
  showFilters = false,
}: PlantVarietySearchProps) {
  const [varieties, setVarieties] = useState<PlantVariety[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedGrowthPattern, setSelectedGrowthPattern] = useState<string>('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const { searchValue: searchQuery, handleChange: handleSearchChange } = useSearchInput({
    onSearchChange: () => {}, // We handle the search in the useEffect below
    debounceMs: 300,
  });

  // Use debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Search varieties when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim().length >= 2) {
      setIsSearching(true);
      plantApi
        .searchPlantVarieties(debouncedSearchQuery.trim())
        .then(varieties => {
          setVarieties(varieties || []);
        })
        .catch(() => {
          setVarieties([]);
        })
        .finally(() => {
          setIsSearching(false);
        });
    } else {
      setVarieties([]);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery]);

  const handleAddNewVariety = (newVariety: PlantVariety) => {
    onChange(newVariety);
    setIsModalOpen(false);
  };

  // Convert varieties to ComboBox options
  // Always include the selected value in options, even if not in search results
  const allVarieties = [...varieties];

  // Add the selected value if it's not already in the varieties array
  if (value && !varieties.some(v => v.id === value.id)) {
    allVarieties.unshift(value);
  }

  const varietyOptions: ComboBoxOption<PlantVariety>[] = allVarieties.map(variety => ({
    value: variety,
    label: `${variety.primaryCommonName} (${variety.species.genus.name} ${variety.species.name}${
      variety.subspecies ? ` subsp. ${variety.subspecies}` : ''
    }${variety.cultivarName ? ` '${variety.cultivarName}'` : ''})`,
  }));

  const renderVarietyOption = (option: ComboBoxOption<PlantVariety>) => {
    const variety = option.value;
    return (
      <div className="flex flex-col">
        <span className="font-medium text-gray-900">{variety.primaryCommonName}</span>
        <span className="text-sm text-gray-500 italic">
          {variety.species.genus.name} {variety.species.name}
          {variety.subspecies && ` subsp. ${variety.subspecies}`}
          {variety.cultivarName && ` '${variety.cultivarName}'`}
        </span>
        <div className="flex flex-wrap gap-1 mt-1">
          {variety.plantType && (
            <span className="text-xs text-gray-400">
              {variety.plantType}
              {variety.category && ` • ${variety.category}`}
            </span>
          )}
          {variety.growthPattern && (
            <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">
              {variety.growthPattern}
            </span>
          )}
          {variety.tags && variety.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {variety.tags.slice(0, 2).map(tag => (
                <span key={tag} className="text-xs bg-green-100 text-green-800 px-1 rounded">
                  {tag}
                </span>
              ))}
              {variety.tags.length > 2 && (
                <span className="text-xs text-gray-400">+{variety.tags.length - 2} more</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const dropdownFooter =
    searchQuery.trim().length >= 2 ? (
      <div className="px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsModalOpen(true)}
          className="w-full justify-start text-green-600 hover:text-green-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add new variety
        </Button>
      </div>
    ) : null;

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
  };

  return (
    <>
      {showFilters && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Filters</span>
            <Button variant="ghost" size="sm" onClick={() => setShowFilterPanel(!showFilterPanel)}>
              <FunnelIcon className="h-4 w-4 mr-1" />
              {showFilterPanel ? 'Hide' : 'Show'} Filters
            </Button>
          </div>

          {showFilterPanel && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-md">
              <div>
                <label className="text-sm font-medium mb-1 block">Growth Pattern</label>
                <Select
                  value={selectedGrowthPattern}
                  onChange={e => setSelectedGrowthPattern(e.target.value)}
                  className="w-full"
                >
                  <option value="">All Growth Patterns</option>
                  <option value="annual">Annual</option>
                  <option value="perennial">Perennial</option>
                  <option value="biennial">Biennial</option>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Special Attributes</label>
                <div className="flex flex-wrap gap-1">
                  {BADGE_TAGS.map(tag => (
                    <Badge
                      key={tag}
                      variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                      className="cursor-pointer text-xs"
                      onClick={() => toggleTag(tag)}
                    >
                      {tag.charAt(0).toUpperCase() + tag.slice(1).replace('-', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              {(selectedTags.length > 0 || selectedGrowthPattern) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedTags([]);
                    setSelectedGrowthPattern('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <ComboBox
        value={value}
        onChange={onChange}
        options={varietyOptions}
        placeholder={placeholder}
        error={error}
        searchable
        onSearch={handleSearchChange}
        searchQuery={searchQuery}
        loading={isSearching}
        emptyMessage={
          searchQuery.trim().length >= 2
            ? `No varieties found for "${searchQuery}"`
            : 'Type at least 2 characters to search'
        }
        renderOption={renderVarietyOption}
        dropdownFooter={dropdownFooter}
        getOptionKey={(variety: PlantVariety) => variety.id}
      />

      {/* Add Plant Variety Modal */}
      <AddPlantVarietyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleAddNewVariety}
        initialSearchQuery={searchQuery}
      />
    </>
  );
}

export const PlantVarietySearch = memo(PlantVarietySearchComponent);
